import { useCallback, useEffect, useRef, useState } from "react";
import type { MutableRefObject } from "react";
import { normalizeVideoMimeType } from "./videoFormat";

const RECORDING_MIME_TYPE_PREFERENCES = [
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
  "video/mp4",
];
// About 8 MB per minute, which keeps a full 60-second take under the upload limit.
const VIDEO_BITS_PER_SECOND = 1_000_000;
const AUDIO_BITS_PER_SECOND = 64_000;

export type CameraStatus = "off" | "starting" | "live" | "error";

export const useMediaRecorder = ({
  eligibleRef,
  maxDurationSeconds,
  maxRecordingBytes,
}: {
  eligibleRef: MutableRefObject<boolean>;
  maxDurationSeconds: number;
  maxRecordingBytes: number;
}) => {
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>("off");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const liveVideoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cameraStartingRef = useRef(false);
  const isMountedRef = useRef(true);

  const attachLiveVideo = useCallback((element: HTMLVideoElement | null) => {
    liveVideoRef.current = element;
    if (element && streamRef.current) element.srcObject = streamRef.current;
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const releaseStream = useCallback(() => {
    if (liveVideoRef.current) {
      liveVideoRef.current.pause();
      liveVideoRef.current.srcObject = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const stopCamera = useCallback(() => {
    releaseStream();
    if (isMountedRef.current) setCameraStatus("off");
  }, [releaseStream]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearTimer();
      releaseStream();
    };
  }, [clearTimer, releaseStream]);

  const startCamera = useCallback(async () => {
    if (!eligibleRef.current || streamRef.current || cameraStartingRef.current) return;
    cameraStartingRef.current = true;
    setCameraStatus("starting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      if (!eligibleRef.current || !isMountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        if (isMountedRef.current) setCameraStatus("off");
        return;
      }
      streamRef.current = stream;
      if (liveVideoRef.current) liveVideoRef.current.srcObject = stream;
      setCameraStatus("live");
    } catch {
      if (isMountedRef.current) setCameraStatus("error");
    } finally {
      cameraStartingRef.current = false;
    }
  }, [eligibleRef]);

  const stopRecording = useCallback(() => {
    clearTimer();
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  }, [clearTimer]);

  const startRecording = useCallback(
    (onRecordingComplete: (recording: Blob | null) => void) => {
      const stream = streamRef.current;
      if (!stream) return false;

      const requestedMimeType =
        RECORDING_MIME_TYPE_PREFERENCES.find((type) => MediaRecorder.isTypeSupported(type)) || "";
      const recorder = new MediaRecorder(stream, {
        ...(requestedMimeType ? { mimeType: requestedMimeType } : {}),
        videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
      });
      const chunks: Blob[] = [];
      let recordedBytes = 0;
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size === 0) return;
        chunks.push(event.data);
        recordedBytes += event.data.size;
        if (recordedBytes > maxRecordingBytes) stopRecording();
      };
      recorder.onstop = () => {
        clearTimer();
        const uploadMimeType =
          normalizeVideoMimeType(recorder.mimeType) ||
          normalizeVideoMimeType(chunks[0]?.type) ||
          normalizeVideoMimeType(requestedMimeType);
        stopCamera();
        onRecordingComplete(uploadMimeType ? new Blob(chunks, { type: uploadMimeType }) : null);
      };

      recorder.start(1000);
      setElapsedSeconds(0);
      let seconds = 0;
      timerRef.current = setInterval(() => {
        seconds += 1;
        setElapsedSeconds(seconds);
        if (seconds >= maxDurationSeconds) stopRecording();
      }, 1000);
      return true;
    },
    [clearTimer, maxDurationSeconds, maxRecordingBytes, stopCamera, stopRecording]
  );

  return {
    attachLiveVideo,
    cameraStatus,
    elapsedSeconds,
    startCamera,
    stopCamera,
    startRecording,
    stopRecording,
  };
};
